import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TableCell, TableRow, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';
import { PriceFormat } from 'components/UI';
import { VendorCost } from '@spike/product-model';
import ProductVendor from 'model/ProductVendor';
import { FunctionComponent } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { formatPhoneNumber } from '@spike/phone-utils';
import { useMarketplace } from 'hooks';

interface Props {
    vendorCost: VendorCost;
    mobile?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        row: {
            width: '100%'
        },
        cell: {
            'paddingLeft': '0px',
            'paddingTop': '0px',
            'paddingRight': '0px',
            'fontWeight': 500,
            'color': '#7A7A7A',
            [theme.breakpoints.down('md')]: {
                paddingBottom: '16px',
                paddingTop: '16px',
                fontSize: '13px'
            },
            [theme.breakpoints.up('lg')]: {
                paddingBottom: '20px',
                paddingTop: '20px',
                fontSize: '16px'
            },
            '&.MuiTableCell-root': {
                borderBottom: 'none'
            }
        },
        iconCheck: {
            [theme.breakpoints.down('md')]: {
                fontSize: '13px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '16px'
            }
        },
        iconCell: {
            width: '3%'
        },
        normalCell: {
            width: '15%'
        },
        largeCell: {
            width: '25%'
        },
        mobileCard: {
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            width: '100%',
            padding: '12px 0',
            borderBottom: '1px solid #F1F1F1'
        },
        mobileTopRow: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px',
            minWidth: 0
        },
        mobileName: {
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            minWidth: 0,
            flex: 1,
            fontWeight: 600,
            fontSize: '14px',
            lineHeight: '20px',
            color: '#222222'
        },
        mobilePrice: {
            flexShrink: 0,
            fontWeight: 500,
            fontSize: '13px',
            lineHeight: '18px',
            color: '#222222'
        },
        mobileMeta: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: '4px 12px',
            fontSize: '12px',
            lineHeight: '16px',
            color: '#7A7A7A',
            wordBreak: 'break-word'
        },
        mobileMetaItem: {
            minWidth: 0
        }
    })
);

export const VendorRow: FunctionComponent<Props> = props => {
    const classes = useStyles();
    const marketplace = useMarketplace();

    const vendors = useSelector<RootState, Array<ProductVendor>>(state => state.productVendors.list);

    const vendor = vendors.find(v => v.id === props.vendorCost.vendor?.id);
    const address =
        vendor && vendor.address ? `${vendor.address.street},${vendor.address.city},${vendor.address.state}` : '-';
    const phone = formatPhoneNumber(props.vendorCost.vendor?.phone || '-', marketplace.basics.address.country!.id);

    if (props.mobile) {
        return (
            <div className={classes.mobileCard}>
                <div className={classes.mobileTopRow}>
                    <Typography className={classes.mobileName}>
                        {props.vendorCost.default && (
                            <FontAwesomeIcon
                                icon={faCheckCircle}
                                color="#5E8677"
                                className={classes.iconCheck}
                            />
                        )}
                        <span>{props.vendorCost.vendor?.name || '-'}</span>
                    </Typography>
                    <span className={classes.mobilePrice}>
                        <PriceFormat price={props.vendorCost.cost || '-'} />
                    </span>
                </div>
                <div className={classes.mobileMeta}>
                    <span className={classes.mobileMetaItem}>{props.vendorCost.vendor?.contact || '-'}</span>
                    <span className={classes.mobileMetaItem}>{phone}</span>
                </div>
                <div className={classes.mobileMeta}>
                    <span className={classes.mobileMetaItem}>{props.vendorCost.vendor?.email || '-'}</span>
                </div>
                <div className={classes.mobileMeta}>
                    <span className={classes.mobileMetaItem}>{address}</span>
                </div>
            </div>
        );
    }

    return (
        <TableRow className={clsx(classes.row)}>
            <TableCell className={clsx(classes.cell, classes.iconCell)}>
                {props.vendorCost.default && (
                    <FontAwesomeIcon
                        icon={faCheckCircle}
                        color="#5E8677"
                        className={classes.iconCheck}
                    />
                )}
            </TableCell>
            <TableCell className={clsx(classes.cell, classes.normalCell)}>
                {props.vendorCost.vendor?.name || '-'}
            </TableCell>
            <TableCell className={clsx(classes.cell, classes.normalCell)}>
                {props.vendorCost.vendor?.contact || '-'}
            </TableCell>
            <TableCell className={clsx(classes.cell, classes.normalCell)}>{phone}</TableCell>
            <TableCell className={clsx(classes.cell, classes.largeCell)}>
                {props.vendorCost.vendor?.email || '-'}
            </TableCell>
            <TableCell className={clsx(classes.cell, classes.largeCell)}>{address}</TableCell>
            <TableCell className={clsx(classes.cell, classes.normalCell)}>
                <PriceFormat price={props.vendorCost.cost || '-'} />
            </TableCell>
        </TableRow>
    );
};

export default VendorRow;
